import { useCallback, useEffect, useRef, useState } from 'react';
import type { PackageOrder } from '../types';
import type { QueuedNewOrder } from './useNewOrderQueue';
import {
  customerBlacklistService,
  blacklistSubjectOf,
  blacklistKeyOf,
  type BlacklistCheckResult,
  type BlacklistSubject,
} from '../services/customerBlacklistService';

/** Yeni sipariş kontrolünde en fazla bu kadar beklenir; cevap gelmezse sipariş normal akar. */
const NEW_ORDER_TIMEOUT_MS = 3000;
/** Başka kasada yapılan ekleme / çıkarmalar bu aralıkla yansır. */
const REFRESH_MS = 5 * 60 * 1000;
const LIST_DEBOUNCE_MS = 800;
const CHUNK = 200;

/** Kuyruktaki yeni siparişin kara liste bilgisi (QR / kiosk / e-ticaret için null). */
export function queuedBlacklistSubject(q: QueuedNewOrder): BlacklistSubject | null {
  if (q.platform === 'trendyol' && q.trendyolOrder) {
    const o = q.trendyolOrder;
    return {
      platform: 'trendyol',
      orderRef: o.id,
      name: o.customer ? `${o.customer.firstName || ''} ${o.customer.lastName || ''}`.trim() : null,
      phone: o.address?.phone || null,
      address: o.address ? [o.address.address1, o.address.neighborhood, o.address.district].filter(Boolean).join(' ') : null,
      amount: o.totalPrice ?? null,
    };
  }
  if ((q.platform === 'yemeksepeti' || q.platform === 'migros' || q.platform === 'getir') && q.masaSiparisi?.id) {
    const m = q.masaSiparisi;
    return {
      platform: q.platform,
      orderRef: String(m.id),
      name: m.musteriAdi || null,
      phone: m.musteriTelefon || null,
      address: m.musteriAdres || null,
      amount: m.toplamTutar ?? null,
    };
  }
  return null;
}

const withTimeout = <T,>(p: Promise<T>, ms: number): Promise<T> =>
  new Promise((resolve, reject) => {
    const timer = window.setTimeout(() => reject(new Error('timeout')), ms);
    p.then(v => { window.clearTimeout(timer); resolve(v); }, e => { window.clearTimeout(timer); reject(e); });
  });

/**
 * Paket ekranı kara liste kontrolü: listedeki siparişler (etiket), yeni gelen sipariş
 * (uyarı penceresi + otomatik onayın durması) ve telefon müşterisi.
 */
export function useCustomerBlacklist(branchId: string | null | undefined, orders: PackageOrder[], enabled = true) {
  const [results, setResults] = useState<Map<string, BlacklistCheckResult>>(() => new Map());
  const [loadError, setLoadError] = useState(false);
  const checkedRef = useRef<Set<string>>(new Set());
  const ordersRef = useRef<PackageOrder[]>(orders);
  ordersRef.current = orders;
  const branch = branchId || null;

  const merge = useCallback((list: BlacklistCheckResult[], replace = false) => {
    setResults(prev => {
      const next = replace ? new Map<string, BlacklistCheckResult>() : new Map(prev);
      for (const r of list) {
        const key = blacklistKeyOf({ platform: r.platform as BlacklistSubject['platform'], orderRef: r.orderRef });
        if (!key) continue;
        if (r.match || r.possible) next.set(key, r);
        else next.delete(key);
      }
      return next;
    });
  }, []);

  const runChecks = useCallback(async (subjects: BlacklistSubject[], replace: boolean) => {
    const all: BlacklistCheckResult[] = [];
    for (let i = 0; i < subjects.length; i += CHUNK) {
      all.push(...await customerBlacklistService.check(subjects.slice(i, i + CHUNK), branch, false));
    }
    merge(all, replace);
  }, [branch, merge]);

  // Listede yeni görünen siparişleri kontrol et (bir kez)
  useEffect(() => {
    if (!enabled) return;
    const timer = window.setTimeout(() => {
      const fresh = ordersRef.current
        .map(blacklistSubjectOf)
        .filter((s): s is BlacklistSubject => !!s && !!s.orderRef && !checkedRef.current.has(blacklistKeyOf(s)));
      if (fresh.length === 0) return;
      fresh.forEach(s => checkedRef.current.add(blacklistKeyOf(s)));
      runChecks(fresh, false)
        .then(() => setLoadError(false))
        .catch(e => {
          console.warn('[KARA LİSTE] Liste kontrolü yapılamadı:', e);
          fresh.forEach(s => checkedRef.current.delete(blacklistKeyOf(s)));
          setLoadError(true);
        });
    }, LIST_DEBOUNCE_MS);
    return () => window.clearTimeout(timer);
  }, [orders, enabled, runChecks]);

  /** Bütün listeyi baştan kontrol et (ekleme / çıkarma sonrası ve 5 dakikada bir). */
  const refreshAll = useCallback(async () => {
    if (!enabled) return;
    const subjects = ordersRef.current
      .map(blacklistSubjectOf)
      .filter((s): s is BlacklistSubject => !!s && !!s.orderRef);
    try {
      await runChecks(subjects, true);
      checkedRef.current = new Set(subjects.map(blacklistKeyOf));
      setLoadError(false);
    } catch (e) {
      console.warn('[KARA LİSTE] Yenileme yapılamadı:', e);
      setLoadError(true);
    }
  }, [enabled, runChecks]);

  useEffect(() => {
    if (!enabled) return;
    const timer = window.setInterval(() => { void refreshAll(); }, REFRESH_MS);
    return () => window.clearInterval(timer);
  }, [enabled, refreshAll]);

  // Şube değişince önbellek sıfırlanır
  useEffect(() => {
    checkedRef.current = new Set();
    setResults(new Map());
  }, [branch]);

  /**
   * Yeni gelen sipariş: sunucuda kayda geçer (record). En fazla 3 sn beklenir;
   * cevap gelmezse null döner ve sipariş normal akışla devam eder.
   */
  const checkNewOrder = useCallback(async (subject: BlacklistSubject): Promise<BlacklistCheckResult | null> => {
    if (!enabled) return null;
    try {
      const [r] = await withTimeout(customerBlacklistService.check([subject], branch, true), NEW_ORDER_TIMEOUT_MS);
      if (r) {
        merge([r]);
        checkedRef.current.add(blacklistKeyOf(subject));
      }
      setLoadError(false);
      return r && (r.match || r.possible) ? r : null;
    } catch (e) {
      console.warn('[KARA LİSTE] Yeni sipariş kontrol edilemedi:', e);
      setLoadError(true);
      return null;
    }
  }, [enabled, branch, merge]);

  /** Telefon müşterisi (arama / seçili cari): siparişsiz kontrol. */
  const checkCustomer = useCallback(async (subject: BlacklistSubject): Promise<BlacklistCheckResult | null> => {
    if (!enabled || (!subject.phone && !subject.accountId)) return null;
    try {
      const [r] = await customerBlacklistService.check([subject], branch, false);
      return r && (r.match || r.possible) ? r : null;
    } catch (e) {
      console.warn('[KARA LİSTE] Müşteri kontrol edilemedi:', e);
      return null;
    }
  }, [enabled, branch]);

  const resultForOrder = useCallback((order: PackageOrder | null | undefined): BlacklistCheckResult | null => {
    const key = blacklistKeyOf(blacklistSubjectOf(order));
    return key ? results.get(key) ?? null : null;
  }, [results]);

  return { results, resultForOrder, checkNewOrder, checkCustomer, refreshAll, loadError };
}
